/**
 * Capability gate for on-demand report_device_info.
 *
 * Player version is NEVER sufficient proof that the device can ACK.
 * Fail closed when Fleet does not advertise explicit command support.
 */

export const REPORT_DEVICE_INFO_COMMAND = "report_device_info";

export type DeviceInfoReportCapabilityEvidence = {
  supportsReportDeviceInfo?: boolean | null;
  capabilities?: { reportDeviceInfo?: boolean | null } | null;
  commandSupport?: string[] | null;
  /** Ignored. Version is not proof of command support. */
  playerVersion?: string | null;
};

export type DeviceInfoReportCapabilityDecision =
  | { ok: true; reason: "supported" }
  | { ok: false; reason: "unsupported" }
  | { ok: false; reason: "missing_evidence" };

function advertisedTrue(value: boolean | null | undefined): boolean {
  return value === true;
}

function advertisedFalse(value: boolean | null | undefined): boolean {
  return value === false;
}

export function canRequestReportDeviceInfo(
  evidence: DeviceInfoReportCapabilityEvidence | null | undefined
): DeviceInfoReportCapabilityDecision {
  const src = evidence || {};
  const listed = Array.isArray(src.commandSupport)
    ? src.commandSupport.includes(REPORT_DEVICE_INFO_COMMAND)
    : false;

  if (
    advertisedTrue(src.supportsReportDeviceInfo) ||
    advertisedTrue(src.capabilities?.reportDeviceInfo) ||
    listed
  ) {
    return { ok: true, reason: "supported" };
  }

  if (
    advertisedFalse(src.supportsReportDeviceInfo) ||
    advertisedFalse(src.capabilities?.reportDeviceInfo)
  ) {
    return { ok: false, reason: "unsupported" };
  }

  return { ok: false, reason: "missing_evidence" };
}

export function shouldSendReportOnExpand(args: {
  expanded: boolean;
  deviceId?: string | null;
  cacheMiss: boolean;
  authorized: boolean;
  capability: DeviceInfoReportCapabilityDecision;
}): boolean {
  return Boolean(
    args.expanded &&
      args.deviceId &&
      args.cacheMiss &&
      args.authorized &&
      args.capability.ok
  );
}
